import {
  APP_ID,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  signal,
} from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { bootstrapApplication, DomSanitizer } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { AgrBrowserModule, I18nBrowserEffects } from '@next/core-lib/browser';
import { AgrNavigationRailModule } from '@next/core-lib/components';
import { IgnoreStatusOfflinePatterns } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects } from '@next/core-lib/i18n';
import { provideAgrNavigation, NavigationEffects } from '@next/core-lib/navigation';
import { AgrPWAModule } from '@next/core-lib/pwa';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { SettingsService } from '@next/core-lib/settings';
import { SiteEffects } from '@next/core-lib/site';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';

import { AnimalGroupEffects } from './app/animal-group/store/animal-group.effects';
import * as animalGroupReducer from './app/animal-group/store/animal-group.reducer';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { DeliveryOverviewResolver } from './app/delivery/resolvers/delivery-overview.resolver';
import { DeliveryEffects } from './app/delivery/store/delivery.effects';
import * as deliveryReducer from './app/delivery/store/delivery.reducer';
import { FecalScoringResolver } from './app/fecal-scoring/resolvers/fecal-scoring.resolver';
import { FecalScoringEffects } from './app/fecal-scoring/store/effects/fecal-scoring.effects';
import * as fecalScoringReducer from './app/fecal-scoring/store/reducers/fecal-scoring.reducer';
import { FeedAdviceOverviewResolver } from './app/feed-advice/resolvers/feed-advice-overview.resolver';
import { FeedAdviceResolver } from './app/feed-advice/resolvers/feed-advice.resolver';
import { FeedAdviceEffects } from './app/feed-advice/store/feed-advice.effects';
import * as feedAdviceReducer from './app/feed-advice/store/feed-advice.reducer';
import { FeedProfitOverviewResolver } from './app/feed-profit/resolvers/feed-profit-overview.resolver';
import { FeedProfitEffects } from './app/feed-profit/store/feed-profit.effects';
import * as feedProfitReducer from './app/feed-profit/store/feed-profit.reducer';
import { ProductResolver } from './app/product/resolvers/product.resolver';
import { ProductEffects } from './app/product/store/effects/product.effects';
import * as productReducer from './app/product/store/reducers/product.reducer';
import { SettingsBarnResolver } from './app/settings/resolvers/settings-barn.resolver';
import { SettingsDiscountResolver } from './app/settings/resolvers/settings-discount.resolver';
import { SettingsDosatorSettingResolver } from './app/settings/resolvers/settings-dosator.resolver';
import { SettingsUomResolver } from './app/settings/resolvers/settings-uom.resolver';
import { ThirdPartiesLeeOResolver } from './app/settings/resolvers/third-parties-lee-o.resolver';
import { ThirdPartiesNedapResolver } from './app/settings/resolvers/third-parties-nedap-resolver';
import { SettingEffects } from './app/settings/store/effects/settings.effects';
import * as settingsReducer from './app/settings/store/reducers/settings.reducer';
import { SettingsServiceHelper } from './app/shared/services/settingsHelperService';
import { PigEffects } from './app/shared/store/pig.effects';
import { TailBitingResolver } from './app/tail-biting/resolvers/tail-biting.resolver';
import { TailBitingEffects } from './app/tail-biting/store/effects/tail-biting.effects';
import * as tailBitingReducer from './app/tail-biting/store/reducers/tail-biting.reducer';
import { environment } from './environments/environment';
import { parsePigEnvironment } from './environments/parse-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';

    const response = await globalThis.fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parsePigEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

if (environment.PRODUCTION) {
  enableProdMode();
}

setupRuntimeConfig.then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        AgrBrowserModule,
        AgrNavigationRailModule,
        AgrPWAModule,
        MatIconModule,
        ServiceWorkerModule.register('sw-master.js', {
          enabled: environment.PRODUCTION,
        }),
      ),
      provideStore(),
      provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
      provideAgrCore(),
      provideAgrAdvisorRelationshipsSelector(),
      provideAgrElasticApi(),
      provideAgrNavigation(),
      provideAgrWrapperSiteSidenav(),
      provideAgrRouterStore(),
      provideEffects([
        PigEffects,
        CoreEffects,
        I18nEffects,
        I18nBrowserEffects,
        SiteEffects,
        NavigationEffects,
        AnimalGroupEffects,
        DeliveryEffects,
        FecalScoringEffects,
        FeedAdviceEffects,
        FeedProfitEffects,
        ProductEffects,
        SettingEffects,
        TailBitingEffects,
      ]),
      provideState('animalGroupModule', animalGroupReducer.reducer),
      provideState('deliveryModule', deliveryReducer.reducer),
      provideState('fecalScoringModule', fecalScoringReducer.reducer),
      provideState('feedAdviceModule', feedAdviceReducer.reducer),
      provideState('feedProfitModule', feedProfitReducer.reducer),
      provideState('productModule', productReducer.reducer),
      provideState('settingModule', settingsReducer.reducer),
      provideState('tailBitingModule', tailBitingReducer.reducer),
      SettingsService,
      SettingsServiceHelper,
      DeliveryOverviewResolver,
      FeedAdviceResolver,
      FeedAdviceOverviewResolver,
      FeedProfitOverviewResolver,
      ProductResolver,
      SettingsBarnResolver,
      SettingsDiscountResolver,
      SettingsDosatorSettingResolver,
      SettingsUomResolver,
      ThirdPartiesLeeOResolver,
      ThirdPartiesNedapResolver,
      FecalScoringResolver,
      TailBitingResolver,
      {
        provide: APP_ID,
        useFactory: () => loadedConfig().APPLICATION_NAME,
      },
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
      {
        provide: IgnoreStatusOfflinePatterns,
        useValue: [
          '/b2c_1a_allaccountssignupsignin/v2.0/.well-known/openid-configuration',
          '/service/barn/*',
          '/service/business-partner-aggregation/*',
          '/service/navigation-v2/*',
          '/service/concept/*',
          '/service/settings/*',
          '/service/notification/*',
          '/service/notification/hubs/*',
          '/service/notification/hubs/notifications/*',
          '/service/content/*',
          '/service/checkout/*',
          '/service/pig-registration/secure/fecalscore',
          '/service/pig-registration/secure/tailbiting',
        ],
        multi: true,
      },
      provideEnvironmentInitializer(() => {
        const matIcon = inject(MatIconRegistry);
        const sanitizer = inject(DomSanitizer);
        const brandingService = inject(BrandingService);

        matIcon.addSvgIcon(
          'logo-wide',
          sanitizer.bypassSecurityTrustResourceUrl('assets/images/logo-wide.svg'),
        );
        matIcon.addSvgIcon(
          'marker',
          sanitizer.bypassSecurityTrustResourceUrl('assets/images/marker.svg'),
        );
        matIcon.addSvgIcon(
          'mdi-barn',
          sanitizer.bypassSecurityTrustResourceUrl('assets/images/mdi-barn.svg'),
        );

        const faviconElement = document.querySelector(
          'link[rel~="icon"]',
        ) as HTMLLinkElement | null;
        if (faviconElement) {
          faviconElement.href = brandingService.getFaviconUrl();
        }
      }),
    ],
  }).catch((err) => console.error(err));
});
